import React, { useEffect, useRef, useState } from 'react';
import { 
  Typography, 
  Paper, 
  CircularProgress, 
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  makeStyles
} from '@material-ui/core';
import {
  Map as MapIcon,
  Place as PlaceIcon,
  Timeline as TimelineIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Navigation as NavigationIcon
} from '@material-ui/icons';
import { format } from 'date-fns';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

// Fix for the missing icon issue in Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

// Define custom styles
const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: '500px',
    width: '100%',
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  mapLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1000,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  headerIcon: {
    marginRight: theme.spacing(1),
  },
  weatherInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  weatherItem: {
    flex: '1 0 45%',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  detailsGrid: {
    marginTop: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  legendColor: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1),
    borderRadius: 4,
  },
  sprayAreaColor: {
    backgroundColor: 'rgba(0, 255, 0, 0.3)',
  },
  perimeterColor: {
    backgroundColor: 'rgba(255, 0, 0, 0.5)',
  },
  propertyLineColor: {
    backgroundColor: 'rgba(255, 165, 0, 0.5)', // Orange color for property lines
  },
  startMarker: {
    backgroundColor: 'green',
  },
  stopMarker: {
    backgroundColor: 'red',
  },
  gpsStartMarker: {
    backgroundColor: '#4CAF50', // Green color for GPS start markers
  },
  gpsStopMarker: {
    backgroundColor: '#F44336', // Red color for GPS stop markers
  },
  gpsPositionMarker: {
    backgroundColor: '#2196F3', // Blue color for GPS position markers
  },
  legendContainer: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  legendTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  aerialToggle: {
    marginBottom: theme.spacing(2),
  }
}));

// Create custom icon definitions
const createDefaultIcon = () => 
  L.icon({
    iconUrl: icon,
    iconRetinaUrl: iconRetina,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
  });

const createStartIcon = () => 
  L.icon({
    iconUrl: icon,
    iconRetinaUrl: iconRetina,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    className: 'start-icon'
  });

const createStopIcon = () => 
  L.icon({
    iconUrl: icon,
    iconRetinaUrl: iconRetina,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    className: 'stop-icon'
  });

// Create GPS marker icons with different colors
const createGpsMarker = (type) => {
  // Define colors based on type
  let className;
  
  if (type && type.toLowerCase().includes('start')) {
    className = 'gps-start-marker';
  } else if (type && type.toLowerCase().includes('finish')) {
    className = 'gps-stop-marker';
  } else {
    className = 'gps-position-marker';
  }
  
  return L.divIcon({
    className: className,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    html: `<div style="background-color: ${getColorForType(type)}; width: 100%; height: 100%; border-radius: 50%; border: 2px solid white;"></div>`
  });
};

// Function to determine color based on GPS point type
const getColorForType = (type) => {
  if (!type) return '#2196F3'; // Default blue
  
  const lowerType = type.toLowerCase();
  
  if (lowerType.includes('start')) {
    return '#4CAF50'; // Green for start
  } else if (lowerType.includes('finish') || lowerType.includes('completed') || lowerType.includes('finished')) {
    return '#F44336'; // Red for finish/stop
  } else {
    return '#2196F3'; // Blue for other tracking points
  }
};

// Function to convert coordinates from API format to Leaflet format
const convertCoordinates = (paths) => {
  if (!paths || !Array.isArray(paths)) return [];
  
  return paths.map(point => [point.lat, point.lng]);
};

// Function to convert GeoJSON coordinates to Leaflet format
const convertGeoJSONCoordinates = (coordinates) => {
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) return [];
  
  // GeoJSON format has coordinates as [longitude, latitude]
  // Leaflet format needs [latitude, longitude]
  return coordinates[0].map(coord => [coord[1], coord[0]]);
};

// Function to convert GoogleMapsFormat to Leaflet format
const convertGoogleMapsFormat = (googleMapsFormat) => {
  if (!googleMapsFormat || !Array.isArray(googleMapsFormat)) return [];
  
  return googleMapsFormat.map(point => [point.lat, point.lng]);
};

// Format time for display
const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  
  // Check if timeString is in ISO format or just a time string
  if (timeString.includes('T')) {
    return format(new Date(timeString), 'h:mm a');
  } else {
    // Parse time string like "7:40 AM"
    return timeString;
  }
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  } catch (error) {
    return dateString;
  }
};

const MapView = ({ jobData, propertyLines, gpsData, chemicals, activeSprayLog, woNotes=[] }) => {
  console.log('MapView...235...', jobData, activeSprayLog)
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [useAerial, setUseAerial] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersRef = useRef([]);
  const polygonsRef = useRef([]);
  const propertyLinesLayersRef = useRef([]);
  const gpsMarkersRef = useRef([]);

  const activeSprayOperation = jobData?.sprayOperations?.find(so => (so?.operationId === activeSprayLog?.operationId))
  console.log('The active Spray Operation!!', activeSprayOperation)
  // console.log('Map View...', jobData);
  // console.log('Property Lines...', propertyLines);
  // console.log('GPS Data', gpsData);

  // Extract coordinates for center of map
  const getMapCenter = () => {
    if (!jobData || !jobData.jobSite || !jobData.jobSite.location) {
      return [33.4646871, -111.996313]; // Default to coordinates in the data
    }
    
    const { coordinates } = jobData.jobSite.location;
    return [coordinates[1], coordinates[0]]; // Convert from [lng, lat] to [lat, lng] for Leaflet
  };
  
  // Process spray areas from the data
  const getSprayAreas = () => {
    if (!jobData || !jobData.sprayOperation || !jobData.sprayOperation.areas) {
      return [];
    }
    
    return jobData.sprayOperation.areas.map(area => {
      return {
        name: area.name,
        coordinates: convertCoordinates(area.paths),
        fillColor: area.fillColor || 'rgba(0, 255, 0, 0.3)',
        strokeColor: area.strokeColor || 'rgba(0, 255, 0, 0.3)'
      };
    });
  };
  
  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    // Set default icon for all markers
    L.Marker.prototype.options.icon = createDefaultIcon();
    
    // Get center coordinates
    const mapCenter = getMapCenter();
    
    // Create map instance if it doesn't exist
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current, {
        center: mapCenter,
        zoom: 18,
        zoomControl: true
      });
      
      // Add attribution control
      L.control.attribution({
        position: 'bottomright'
      }).addTo(mapInstanceRef.current);
      
      // Add zoom control
      L.control.zoom({
        position: 'topright'
      }).addTo(mapInstanceRef.current);
      
      // Add base tile layer (initially OpenStreetMap)
      tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 25
      }).addTo(mapInstanceRef.current);
      
      // Add CSS for GPS markers
      const style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = `
        .gps-start-marker { background-color: #4CAF50 !important; }
        .gps-stop-marker { background-color: #F44336 !important; }
        .gps-position-marker { background-color: #2196F3 !important; }
      `;
      document.getElementsByTagName('head')[0].appendChild(style);
    }
    
    // End loading after a brief delay
    setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  
  // Handle aerial/street view toggle
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    // Function to load ESRI Leaflet
    const loadEsriLayers = async () => {
      try {
        // Remove existing tile layer
        if (tileLayerRef.current) {
          tileLayerRef.current.remove();
          tileLayerRef.current = null;
        }
        
        if (useAerial) {
          // Load ESRI Leaflet dynamically
          const esriLeaflet = await import('esri-leaflet');
          
          // Add ESRI Imagery basemap
          tileLayerRef.current = esriLeaflet.basemapLayer('Imagery').addTo(mapInstanceRef.current);
          
          // Add Maricopa County dynamic map layer
          const dynamicLayer = esriLeaflet.dynamicMapLayer({
            url: "https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Imagery/Aerials/2025/MapServer",
            opacity: 0.7
          }).addTo(mapInstanceRef.current);
          
          // Store reference to clean up later
          tileLayerRef.current._dynamicLayer = dynamicLayer;
        } else {
          // Add OpenStreetMap tile layer
          tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 25
          }).addTo(mapInstanceRef.current);
        }
      } catch (error) {
        console.error("Error loading ESRI Leaflet:", error);
        
        // Fallback to OpenStreetMap if ESRI loading fails
        tileLayerRef.current = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 25
        }).addTo(mapInstanceRef.current);
      }
    };
    
    loadEsriLayers();
    
    // Clean up layers on toggle change
    return () => {
      if (tileLayerRef.current) {
        // Remove dynamic layer if exists
        if (tileLayerRef.current._dynamicLayer) {
          tileLayerRef.current._dynamicLayer.remove();
        }
        tileLayerRef.current.remove();
      }
    };
  }, [useAerial]);
  
  // Add job site marker and spray areas
  useEffect(() => {
    if (!mapInstanceRef.current || !jobData) return;
    
    // Clean up existing markers and polygons
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    polygonsRef.current.forEach(polygon => polygon.remove());
    polygonsRef.current = [];
    
    // Get center coordinates and spray areas
    const mapCenter = getMapCenter();
    const sprayAreas = getSprayAreas();
    
    // Add job site marker
    const siteMarker = L.marker(mapCenter)
      .bindPopup(
        `<strong>${jobData?.jobSite?.name || 'Job Site'}</strong><br>
         ${jobData?.jobSite?.address || 'Address not available'}`
      )
      .addTo(mapInstanceRef.current);
    
    markersRef.current.push(siteMarker);
    
    // Only add these markers if GPS data is not available
    if (!gpsData || gpsData.length === 0) {
      // Add start marker (only if no GPS data available)
      const startPosition = [mapCenter[0] + 0.0002, mapCenter[1] - 0.0002];
      const startMarker = L.marker(startPosition, { icon: createStartIcon() })
        .bindPopup(`<strong>Start Time:</strong> ${formatTime(jobData?.sprayOperation?.startTime)}`)
        .addTo(mapInstanceRef.current);
      
      markersRef.current.push(startMarker);
      
      // Add stop marker (only if no GPS data available)
      const stopPosition = [mapCenter[0] - 0.0002, mapCenter[1] + 0.0002];
      const stopMarker = L.marker(stopPosition, { icon: createStopIcon() })
        .bindPopup(`<strong>Stop Time:</strong> ${formatTime(jobData?.sprayOperation?.stopTime)}`)
        .addTo(mapInstanceRef.current);
      
      markersRef.current.push(stopMarker);
    }
    
    // Add spray areas
    sprayAreas.forEach(area => {
      if (area.coordinates.length > 0) {
        const polygon = L.polygon(area.coordinates, {
          fillColor: area.fillColor,
          fillOpacity: 0.5,
          weight: 2,
          color: area.strokeColor,
          opacity: 0.7
        })
          .bindPopup(`<strong>${area.name}</strong><br>Spray Area`)
          .addTo(mapInstanceRef.current);
        
        polygonsRef.current.push(polygon);
      }
    });
    
    // Center map on job site
    mapInstanceRef.current.setView(mapCenter, 18);
    
    // If we have polygons, fit map to bounds
    if (polygonsRef.current.length > 0) {
      const group = L.featureGroup(polygonsRef.current);
      mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
  }, [jobData]);
  
  // Handle property lines
  useEffect(() => {
    if (!mapInstanceRef.current || !propertyLines || propertyLines.length === 0) return;
    
    // Clean up existing property line layers
    propertyLinesLayersRef.current.forEach(layer => layer.remove());
    propertyLinesLayersRef.current = [];
    
    // Add property lines to the map
    propertyLines.forEach((propertyLine, index) => {
      let coordinates = [];
      
      // Determine which format the coordinates are in and convert accordingly
      if (propertyLine.googleMapsFormat && propertyLine.googleMapsFormat.length > 0) {
        coordinates = convertGoogleMapsFormat(propertyLine.googleMapsFormat);
      } else if (propertyLine.geometry && propertyLine.geometry.coordinates) {
        coordinates = convertGeoJSONCoordinates(propertyLine.geometry.coordinates);
      } else if (propertyLine.paths && propertyLine.paths.length > 0) {
        coordinates = convertCoordinates(propertyLine.paths);
      }
      
      if (coordinates.length > 0) {
        // Create polygon for property line
        const propertyPolygon = L.polygon(coordinates, {
          fillColor: 'rgba(255, 165, 0, 0.3)', // Orange semi-transparent fill
          fillOpacity: 0.3,
          weight: 3,
          color: 'rgba(255, 165, 0, 0.8)', // Orange border
          opacity: 0.8,
          dashArray: '5, 5' // Dashed line style
        });
        
        // Add popup with property information if available
        if (propertyLine.properties || propertyLine.APN || propertyLine.address) {
          const apn = propertyLine.properties?.APN || propertyLine.APN || '';
          const address = propertyLine.properties?.address || propertyLine.address || '';
          
          propertyPolygon.bindPopup(`
            <strong>Property Line</strong><br>
            ${apn ? `APN: ${apn}<br>` : ''}
            ${address ? `Address: ${address}` : ''}
          `);
        }
        
        // Add to map and store reference
        propertyPolygon.addTo(mapInstanceRef.current);
        propertyLinesLayersRef.current.push(propertyPolygon);
      }
    });
    
    // If we have new property lines and no spray areas, fit bounds to property lines
    if (propertyLinesLayersRef.current.length > 0 && polygonsRef.current.length === 0) {
      const group = L.featureGroup(propertyLinesLayersRef.current);
      mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
    
    // If we have both property lines and spray areas, fit bounds to include all
    if (propertyLinesLayersRef.current.length > 0 && polygonsRef.current.length > 0) {
      const allLayers = [...propertyLinesLayersRef.current, ...polygonsRef.current];
      const group = L.featureGroup(allLayers);
      mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
  }, [propertyLines]);
  
// Function to determine color based on GPS point type
const getColorForType = (type) => {
  if (!type) return '#2196F3'; // Default blue
  
  const lowerType = type.toLowerCase();
  
  if (lowerType.includes('start')) {
    return '#4CAF50'; // Green for start
  } else if (lowerType.includes('finish') || lowerType.includes('completed') || lowerType.includes('finished')) {
    return '#F44336'; // Red for finish/stop
  } else {
    return '#2196F3'; // Blue for other tracking points
  }
};

// Function to get marker type label based on GPS point type
const getMarkerTypeLabel = (type) => {
  if (!type) return 'Position';
  
  const lowerType = type.toLowerCase();
  
  if (lowerType.includes('start')) {
    return 'START';
  } else if (lowerType.includes('finish') || lowerType.includes('completed') || lowerType.includes('finished')) {
    return 'END';
  } else {
    return 'Position';
  }
};

// Handle GPS data points
useEffect(() => {
  if (!mapInstanceRef.current || !gpsData || gpsData.length === 0) return;
  
  // Clean up existing GPS markers
  gpsMarkersRef.current.forEach(marker => marker.remove());
  gpsMarkersRef.current = [];
  
  // Sort GPS data by created_at to ensure chronological order
  const sortedGpsData = [...gpsData].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateA - dateB;
  });
  
  // Create array to store coordinates for polyline
  const pathCoordinates = [];
  
  // Add markers for each GPS point
  sortedGpsData.forEach((point, index) => {
    if (!point.lat || !point.long) return;
    
    const position = [parseFloat(point.lat), parseFloat(point.long)];
    pathCoordinates.push(position);
    
    // Get the marker color and type label
    const markerColor = getColorForType(point.type);
    const markerTypeLabel = getMarkerTypeLabel(point.type);
    
    // Determine marker type and style
    const markerIcon = L.divIcon({
      className: 'custom-div-icon',
      html: `<div style="background-color: ${markerColor}; 
                        width: 10px; 
                        height: 10px; 
                        border-radius: 50%; 
                        border: 2px solid white;"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7]
    });
    
    // Create marker with popup info
    const marker = L.marker(position, { icon: markerIcon })
      .bindPopup(`
        <strong>${markerTypeLabel}</strong><br>
        Time: ${formatDate(point.created_at)}<br>
        ${point.speed ? `Speed: ${point.speed} mph<br>` : ''}
        ${point.heading ? `Heading: ${point.heading}°<br>` : ''}
        ${point.accuracy ? `Accuracy: ${point.accuracy} m<br>` : ''}
        ${point.notes ? `Notes: ${point.notes}` : ''}
      `)
      .addTo(mapInstanceRef.current);
    
    gpsMarkersRef.current.push(marker);
    
    // Add arrow for direction if heading is available and speed > 0
    if (point.heading && parseFloat(point.speed) > 0) {
      const arrowIcon = L.divIcon({
        className: 'heading-arrow',
        html: `<div style="transform: rotate(${point.heading}deg); 
                          width: 0; 
                          height: 0; 
                          border-left: 6px solid transparent; 
                          border-right: 6px solid transparent; 
                          border-bottom: 12px solid rgba(0, 0, 0, 0.5);"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      });
      
      const arrowMarker = L.marker(position, { icon: arrowIcon }).addTo(mapInstanceRef.current);
      gpsMarkersRef.current.push(arrowMarker);
    }
    
    // For START and END points, add a label
    if (markerTypeLabel === 'START' || markerTypeLabel === 'END') {
      const labelIcon = L.divIcon({
        className: 'gps-label',
        html: `<div style="
          background-color: ${markerColor};
          color: white;
          font-weight: bold;
          font-size: 10px;
          padding: 2px 4px;
          border-radius: 3px;
          white-space: nowrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          transform: translate(-50%, -150%);
        ">${markerTypeLabel}</div>`,
        iconSize: [40, 20],
        iconAnchor: [20, 0]
      });
      
      const labelMarker = L.marker(position, { icon: labelIcon }).addTo(mapInstanceRef.current);
      gpsMarkersRef.current.push(labelMarker);
    }
  });
  
  // Create a path line connecting GPS points
  if (pathCoordinates.length > 1) {
    const pathLine = L.polyline(pathCoordinates, {
      color: '#3388ff',
      weight: 3,
      opacity: 0.7,
      dashArray: '5, 10'
    }).addTo(mapInstanceRef.current);
    
    gpsMarkersRef.current.push(pathLine);
  }
  
  // If we have GPS markers, fit bounds to include them
  if (gpsMarkersRef.current.length > 0) {
    // Create a temporary feature group for all visible layers
    const allLayers = [
      ...gpsMarkersRef.current,
      ...polygonsRef.current,
      ...propertyLinesLayersRef.current
    ].filter(layer => layer);
    
    if (allLayers.length > 0) {
      const group = L.featureGroup(allLayers);
      mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
  }
}, [gpsData]);


  
  // Extract weather data
  const weather = jobData?.sprayOperation?.weatherData;
  let pdfURL = null
  if (activeSprayLog?._id) {
    // console.log('Do we havepdf url??', activeSprayLog)
    pdfURL = `https://dflspaces.sfo2.digitaloceanspaces.com/spraylog/${activeSprayLog._id}.pdf`
  }
  const handleViewPDF = () => {
    // let pdfURL = `https://dflspaces.sfo2.digitaloceanspaces.com/spraylog/${log._id}.pdf`
    window.open(pdfURL, '_blank')
  }

  return (
    <div>
      <Card className={classes.aerialToggle}>
        <CardContent>
          <Typography variant="h6" className={classes.cardHeader}>
            <MapIcon className={classes.headerIcon} />
            Map View
          </Typography>
          
          <Button 
            variant={useAerial ? "contained" : "outlined"} 
            color="primary"
            onClick={() => setUseAerial(!useAerial)}
          >
            {useAerial ? "Switch to Street Map" : "Switch to Aerial View"}
          </Button>
        </CardContent>
      </Card>
      
      <Paper className={classes.mapContainer}>
        {loading && (
          <div className={classes.mapLoadingOverlay}>
            <CircularProgress />
          </div>
        )}
        
        {/* This div will become the map container */}
        <div 
          ref={mapContainerRef} 
          style={{ height: '100%', width: '100%' }}
        />
      </Paper>
      
      {/* Map Legend */}
      <Card className={classes.legendContainer}>
        <CardContent>
          <Typography variant="subtitle1" className={classes.legendTitle}>
            Map Legend
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.legendItem}>
                <div className={`${classes.legendColor} ${classes.sprayAreaColor}`}></div>
                <Typography variant="body2">Spray Areas</Typography>
              </div>
              
              <div className={classes.legendItem}>
                <div className={`${classes.legendColor} ${classes.propertyLineColor}`}></div>
                <Typography variant="body2">Property Lines</Typography>
              </div>
              
              <div className={classes.legendItem}>
                <div className={`${classes.legendColor} ${classes.gpsPositionMarker}`}></div>
                <Typography variant="body2">GPS Position</Typography>
              </div>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <div className={classes.legendItem}>
                <div className={`${classes.legendColor} ${classes.gpsStartMarker}`}></div>
                <Typography variant="body2">Start Location</Typography>
              </div>
              
              <div className={classes.legendItem}>
                <div className={`${classes.legendColor} ${classes.gpsStopMarker}`}></div>
                <Typography variant="body2">Stop Location</Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Info Sections */}
      <Grid container spacing={3} className={classes.detailsGrid}>
        {/* Weather Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" className={classes.cardHeader}>
                <TimelineIcon className={classes.headerIcon} />
                Weather Conditions
              </Typography>
              
              <Divider style={{ marginBottom: '16px' }} />
              
              <div className={classes.weatherInfo}>
                {weather ? (
                  <>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Temperature:</Typography>
                      <Typography variant="body1" component="div">
                        {weather.main?.temp && `${Math.round(weather.main.temp)}°F`}
                      </Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Weather:</Typography>
                      <Typography variant="body1" component="div">
                        {weather.weather && weather.weather[0]?.description}
                      </Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Wind:</Typography>
                      <Typography variant="body1" component="div">
                        {weather.wind && `${weather.wind.speed} mph`}
                      </Typography>
                    </div>
                    
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Humidity:</Typography>
                      <Typography variant="body1" component="div">
                        {weather.main?.humidity && `${weather.main.humidity}%`}
                      </Typography>
                    </div>
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Weather data not available
                  </Typography>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Spray Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <Typography variant="subtitle1" className={classes.cardHeader}>
                  <TimelineIcon className={classes.headerIcon} />
                  Operation Details
                </Typography>
                <Button
                    startIcon={<PictureAsPdfIcon />}
                    variant="outlined"
                    color="primary"
                    onClick={handleViewPDF}
                    disabled={!pdfURL}
                  >
                    View PDF
                </Button>
              </div>
              {/* <Typography variant="subtitle1" className={classes.cardHeader}>
                <TimelineIcon className={classes.headerIcon} />
                Operation Details
              </Typography> */}
              
              <Divider style={{ marginBottom: '16px' }} />
              
              <div className={classes.weatherInfo}>
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Start Time:</Typography>
                  <Typography variant="body1" component="div">
                    {formatTime(activeSprayOperation?.startTime)}
                  </Typography>
                </div>
                
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Stop Time:</Typography>
                  <Typography variant="body1" component="div">
                    {formatTime(activeSprayOperation?.stopTime)}
                  </Typography>
                </div>
                
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Spray Type:</Typography>
                  <Typography variant="body1" component="div">
                    {activeSprayOperation?.sprayType || 'N/A'}
                  </Typography>
                </div>
                
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Target:</Typography>
                  <Typography variant="body1" component="div">
                    {activeSprayOperation?.target || 'N/A'}
                  </Typography>
                </div>
                
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Area Size:</Typography>
                  <Typography variant="body1" component="div">
                    {activeSprayOperation?.size && `${activeSprayOperation.size} ${activeSprayOperation.unit}`}
                  </Typography>
                </div>
                
                <div className={classes.weatherItem}>
                  <Typography variant="body2" component="span">Water Volume:</Typography>
                  <Typography variant="body1" component="div">
                    {activeSprayOperation?.waterUsed ? `${activeSprayOperation.waterUsed} gallons` : activeSprayOperation?.waterVolume ? activeSprayOperation.waterVolume : (activeSprayLog?.waterStart && activeSprayLog?.waterEnd) ? `${activeSprayLog.waterStart - activeSprayLog.waterEnd} gallons` : ''}
                  </Typography>
                </div>
              </div>
              <Divider style={{ marginBottom: '16px', marginTop: '16px' }} />
              <Typography variant="subtitle1" className={classes.cardHeader}>
                  <TimelineIcon className={classes.headerIcon} />
                  Workorder Notes
                </Typography>
                {woNotes?.length ? (
                  woNotes.map((note) => {
                    const createdAt = new Date(note.created_at).toLocaleString(); // format date

                    return (
                      <div key={note._id} className={classes.weatherInfo}>
                        <div className={classes.weatherItem}>
                          <Typography variant="body2" component="p" gutterBottom>
                            {note.notes}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" component="div">
                            {createdAt} &mdash; {note.created_by_name} ({note.crewName})
                          </Typography>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="textSecondary">No notes available.</Typography>
                )}


            </CardContent>
          </Card>
        </Grid>
        
        {/* Chemical Mixture Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" className={classes.cardHeader}>
                <TimelineIcon className={classes.headerIcon} />
                Chemical Mixture
              </Typography>
              
              <Divider style={{ marginBottom: '16px' }} />
              
              {activeSprayLog?.chemicalDetails && 
              activeSprayLog.chemicalDetails.length > 0 ? (
                <Grid container spacing={2}>
                  {activeSprayLog.chemicalDetails.map((chemical, index) => {
                    const chemicalDetails = getChemicalDetails(chemical, chemicals)
                    console.log('889...chemicalDetails', chemicalDetails)
                    return(
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Paper className={classes.weatherItem} style={{ height: '100%' }}>
                        <Typography variant="body2" gutterBottom>
                          Chemical Name: {chemicalDetails?.name}
                        </Typography>
                        <Typography variant="body2">
                          Volume: {chemical.amountUsed} {chemical.unit}
                        </Typography>
                        
                        {/* <Typography variant="body2">
                          Per: {chemical.per}
                        </Typography> */}
                        {/* <Typography variant="body2">
                          Water Volume: {activeSprayOperation?.waterUsed ? `${activeSprayOperation.waterUsed} gallons` : activeSprayOperation?.waterVolume ? activeSprayOperation.waterVolume : (activeSprayLog?.waterStart && activeSprayLog?.waterEnd) ? `${activeSprayLog.waterStart - activeSprayLog.waterEnd} gallons` : ''}
                gallons
                        </Typography> */}
                        {/* <Typography variant="body2">
                          Pre-Mix: {chemical.isPreMix ? 'Yes' : 'No'}
                        </Typography> */}
                      </Paper>
                    </Grid>
                  )})}
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No chemical mixture data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* GPS Data Summary - only show if available */}
        {gpsData && gpsData.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" className={classes.cardHeader}>
                  <NavigationIcon className={classes.headerIcon} />
                  GPS Tracking Data
                </Typography>
                
                <Divider style={{ marginBottom: '16px' }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Points Recorded:</Typography>
                      <Typography variant="body1" component="div">
                        {gpsData.length}
                      </Typography>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">First Recording:</Typography>
                      <Typography variant="body1" component="div">
                        {gpsData.length > 0 ? formatDate(gpsData[0]?.created_at) : 'N/A'}
                      </Typography>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Last Recording:</Typography>
                      <Typography variant="body1" component="div">
                        {gpsData.length > 0 ? formatDate(gpsData[gpsData.length - 1]?.created_at) : 'N/A'}
                      </Typography>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.weatherItem}>
                      <Typography variant="body2" component="span">Average Accuracy:</Typography>
                      <Typography variant="body1" component="div">
                        {gpsData.length > 0 
                          ? `${(gpsData.reduce((sum, point) => sum + (parseFloat(point.accuracy) || 0), 0) / gpsData.length).toFixed(2)} meters` 
                          : 'N/A'}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Notes */}
        {jobData?.sprayOperation?.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" className={classes.cardHeader}>
                  <TimelineIcon className={classes.headerIcon} />
                  Notes
                </Typography>
                
                <Divider style={{ marginBottom: '16px' }} />
                
                <Typography variant="body2">
                  {jobData.sprayOperation.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

const getChemicalDetails = (chemical, chemicals) => {
  console.log('Get chemical details...', chemical)
  console.log(chemicals)
  if (chemical?.productName) {
    return {
      ...chemical,
      name: chemical.productName
    }
  }
  let chem = {
    ...chemical,
    name: 'Unknown Chemical'
  }
  let chemItem = chemicals.find(c => (c._id.toString() === chemical.selectedChemical))
  // console.log('chem item', chemItem)
  if (chemItem) {
    chem.name = `${chemItem.name}${chemItem?.altName ? `(${chemItem.altName})` : ''}`
  }
  return chem
}

export default MapView;
